/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  ActionReference,
  ConditionFunctionConfig,
  ConditionsConfig,
  ConditionsConfig_ConditionConfig,
  ConditionsConfig_ConditionsConfigGroup,
  Editor_AllowedValues,
  InputConfig,
  InputDescription,
  OutputDescription,
  OutputReference,
  ValueConfig_V,
} from "@smartsuite/automation-engine-api";
import {
  Editor_Validation,
  unwrapValueConfig,
} from "@smartsuite/automation-engine-api";
import type { ContentItem, Doc } from "@smartsuite/smartdoc";
import type { MemberScope } from "../../../utils/member-scope";
import {
  memberScopeForInput,
  solutionMemberScope,
} from "../../../utils/member-scope";
import { recordSourceMissing } from "../../../utils/record-source";
import {
  isEmptySmartdoc,
  parseSmartdoc,
} from "@smartsuite/automation-conditions";
import { t } from "i18next";
import * as R from "remeda";
import { parseSmartDoc } from "../../../containers/AutomationForm/Forms/InputForm/FieldEditor/FieldEditorRow/fields/SmartDocEditor/to-value-config";
import {
  isSupportedOutput,
  memoizedConvertOutputToDynamicSectionOption,
  isFindRecordsOne,
} from "../../../containers/AutomationForm/Forms/InputForm/utils";
import type {
  ActionCandidate,
  ActionGroupCandidate,
  AutomationCandidate,
  BranchCandidate,
  BranchesActionGroupCandidate,
  InputCandidate,
} from "../../../types/candidate";
import { isOptionExists } from "../../../utils/check-option-exists";
import { getOutputDescriptions } from "../../../utils/conditions-coverter.hook";
import { compareActionReferences } from "../../../utils/conditions-coverters";
import {
  TriggerLabel,
  ValidationType,
  FIND_RECORD_LIMIT_ID,
  FIND_RECORD_LIMIT_SORT_ID,
  INPUT_ACTION_NAME_ID,
  CURRENT_ITEM_OUTPUT_ID,
  WHEN_BUTTON_IS_CLICKED_BUTTON_SLUG,
  WHEN_BUTTON_IS_CLICKED_BUTTON_LABEL,
} from "../../../utils/constants";
import {
  isRecordAction,
  countFieldsGroupWithValues,
} from "../../../utils/action-helpers";
import {
  findRecordsActionsToOptions,
  triggerToOption,
} from "../../../containers/AutomationForm/Forms/InputForm/RecordsSourceEditor/utils";
import { validateScheduleInput } from "./validate-schedule";
import { resolveReferencedConditionField } from "../../../containers/AutomationForm/Sections/TriggerSection/ConditionsForm/ConditionItem/left-source/resolve-referenced-condition-field";
import type { CandidateStore } from "../candidate.store";
import { validateKeyValueInput } from "./validate-key-value";
import { rootStore } from "../..";
import type { LoopScope } from "../looping.store";
import type { DynamicSectionOption } from "../../../types/line";
import { validateAttachmentsInput } from "./validate-attachments";
import { selectOptionMatchesStoredValue } from "./selectStoredSelectOption";
import {
  collectActionsDeep,
  locateEnclosingLoop,
  walkActionGroups,
} from "./action-group-walk";
import { isSelectOptionDisabled } from "./selectOptionStatus";

export const triggersIdsAvailableForMergeRecordsAction = [
  TriggerLabel.createdRecord as string,
  TriggerLabel.updatedRecord as string,
  TriggerLabel.formSubmitted as string,
  TriggerLabel.matchesCondition as string,
];

export const allowedValueValidation = (
  value: string,
  allowedValues: Editor_AllowedValues,
): string => {
  const isValid = allowedValues.values.includes(value);
  if (isValid || value === "") {
    return "";
  }
  return t("automation-form.this-value-is-not-allowed");
};

export const regexValidation = (value: string, regex: string): string => {
  const isValid = value.match(regex);

  if (isValid != null || value === "") {
    return "";
  }
  if (regex === ValidationType.required) {
    return t("automation-form.this-field-is-required");
  }
  return t("automation-form.this-field-is-not-valid");
};

const stateValidation = (state: Editor_Validation): string => {
  const isValid = state === Editor_Validation.ANY;
  return isValid ? "" : t("automation-form.this-field-is-not-valid");
};

const maxStringLengthValidation = (
  values: ValueConfig_V[],
  allowedLength: number,
): string => {
  const stringValues = values.filter(
    (value) => value.value.oneofKind === "string",
  );
  const stringLengths = stringValues.map((value) => {
    if (value.value.oneofKind !== "string") {
      return 0;
    }
    return value.value.string.length;
  });
  const sum = stringLengths.reduce((partialSum, a) => partialSum + a, 0);
  return sum > allowedLength
    ? t("automation-form.max-length-validation", { length: allowedLength })
    : "";
};

const urlValidation = (value: string): string => {
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return "";
  } catch (_) {
    return t("automation-form.this-field-is-not-valid");
  }
};

export const checkInputValidation = (
  inputDescription: InputDescription,
  input: InputCandidate,
): string => {
  // return validation error or ""
  const values: ValueConfig_V[] = input?.value?.values ?? [];
  const editorDescription = inputDescription.editor?.editor;

  if (R.isTruthy(input?.validationError)) {
    return input?.validationError;
  }

  if (editorDescription?.oneofKind === "limitAndSort") {
    const limitSortValue = input.value?.values?.[0]?.value;
    if (limitSortValue?.oneofKind !== FIND_RECORD_LIMIT_SORT_ID) {
      return t("automation-form.this-field-is-not-valid");
    }

    const isSortByFieldExists =
      limitSortValue.limitSort.sortBy === "" ||
      editorDescription.limitAndSort.fields.some(
        (field) => field.id === limitSortValue.limitSort.sortBy,
      );

    if (!isSortByFieldExists) {
      return t("automation-form.deleted-field-validation-message");
    }
  }

  if (editorDescription?.oneofKind === "select") {
    const hasMissingSelectedOption = values.some((value) => {
      if (value.value.oneofKind !== "string") {
        return false;
      }

      const selectedValue = value.value.string;
      return (
        selectedValue !== "" &&
        !editorDescription.select.options.some((option) =>
          selectOptionMatchesStoredValue(
            option.value,
            selectedValue,
            editorDescription.select.type,
          ),
        )
      );
    });

    if (hasMissingSelectedOption) {
      return t("automation-form.deleted-field-validation-message");
    }

    // A selected option whose connection/credential is disabled ("unavailable")
    // is not a usable value. The selection is kept visible (greyed) but must be
    // treated as invalid so the field shows the required error and the
    // automation cannot be saved with an unavailable connection selected.
    const hasDisabledSelectedOption = values.some((value) => {
      if (value.value.oneofKind !== "string") {
        return false;
      }

      const selectedValue = value.value.string;
      return (
        selectedValue !== "" &&
        editorDescription.select.options.some(
          (option) =>
            isSelectOptionDisabled(option.status) &&
            selectOptionMatchesStoredValue(
              option.value,
              selectedValue,
              editorDescription.select.type,
            ),
        )
      );
    });

    if (hasDisabledSelectedOption) {
      return t("automation-form.this-field-is-required");
    }
  }

  if (
    editorDescription?.oneofKind === "keyValue" &&
    editorDescription.keyValue.keyType?.oneofKind === "select"
  ) {
    const keyOptions = editorDescription.keyValue.keyType.select.options;
    const hasMissingSelectedKey = values.some((value) => {
      if (value.value.oneofKind !== "keyValue") {
        return false;
      }

      return (value.value.keyValue.key?.values ?? []).some((keyValue) => {
        if (keyValue.value.oneofKind !== "string") {
          return false;
        }

        const keyString = keyValue.value.string;
        return (
          keyString !== "" &&
          !keyOptions.some((option) => option.value === keyString)
        );
      });
    });

    if (hasMissingSelectedKey) {
      return t("automation-form.deleted-field-validation-message");
    }
  }

  // check json editor "must be a top-level object" constraint
  if (
    editorDescription?.oneofKind === "json" &&
    editorDescription.json.requireObject
  ) {
    // Object values are stored wrapped (oneofKind "valueConfigJson"), so unwrap
    // before inspecting the leaf -- otherwise a valid object never looks like a
    // "keyValue" and the constraint always fails.
    const leaf =
      input.value !== undefined
        ? unwrapValueConfig(input.value).values[0]?.value
        : undefined;
    if (
      leaf &&
      leaf.oneofKind !== "keyValue" &&
      leaf.oneofKind !== "reference"
    ) {
      return t("automation-form.this-field-must-be-an-object");
    }
  }

  // check required
  if (inputDescription.editor!.required) {
    const filteredValues = values.filter(
      (value) =>
        !(
          (value.value.oneofKind === "string" &&
            (value.value.string == null || value.value.string.trim() === "")) ||
          // A smartdoc editor always stores a serialized document, so an empty
          // one still counts as a value unless its content is inspected.
          (value.value.oneofKind === "smartdoc" &&
            isEmptySmartdoc(parseSmartdoc(value.value.smartdoc))) ||
          (value.value.oneofKind === "smartdocPillsSummary" &&
            isEmptySmartdoc(parseSmartdoc(value.value.smartdocPillsSummary))) ||
          (value.value.oneofKind === "schedule" &&
            !validateScheduleInput(value.value.schedule))
        ),
    );
    if (filteredValues.length === 0) {
      return t("automation-form.this-field-is-required");
    }
  }

  // check attachments validation
  if (
    inputDescription.inputId === "attachments-input" &&
    inputDescription.editor?.editor.oneofKind === "file"
  ) {
    const fileConfig = inputDescription.editor.editor.file;

    return validateAttachmentsInput(values, fileConfig);
  }

  // check checklist validation
  if (values.length > 0) {
    const checklistValues = values.filter(
      (value) => value.value.oneofKind === "checklist",
    );
    if (
      !checklistValues.every(
        (value) =>
          value.value.oneofKind === "checklist" &&
          value.value.checklist.itemsToAdd.every((checklistItemConfig) =>
            checklistItemConfig.text?.values.some(
              (textVal) =>
                (textVal.value.oneofKind === "smartdoc" &&
                  textVal.value.smartdoc.trim() !== "") ||
                (textVal.value.oneofKind === "reference" &&
                  textVal.value.reference.outputId !== "") ||
                (textVal.value.oneofKind === "string" &&
                  textVal.value.string.trim() !== ""),
            ),
          ),
      )
    ) {
      return t("automation-form.this-field-is-required");
    }

    const keyValueValues = values.filter(
      (value) => value.value.oneofKind === "keyValue",
    );
    if (keyValueValues.length > 0) {
      if (
        !keyValueValues.every(
          (value) =>
            value.value.oneofKind === "keyValue" &&
            validateKeyValueInput(value.value.keyValue),
        )
      ) {
        return t("automation-form.this-field-is-required");
      }
    }
  }

  switch (inputDescription.editor?.validation.oneofKind) {
    case "allowedValues": {
      // We can validate only string value
      if (values.every((value) => value.value.oneofKind === "string")) {
        const valueString = values
          .map((value) =>
            value.value.oneofKind === "string" ? value.value.string : "",
          )
          .join("");
        return allowedValueValidation(
          valueString,
          inputDescription.editor.validation.allowedValues,
        );
      }
      // return t("automation-form.this-value-is-not-allowed");
      break;
    }

    case "regex": {
      // We can validate only string value
      if (values.every((value) => value.value.oneofKind === "string")) {
        // validation for email
        if (
          inputDescription.editor.editor.oneofKind === "email" ||
          inputDescription.editor.editor.oneofKind === "phone"
        ) {
          const errors = values
            .map((value) =>
              value.value.oneofKind === "string" &&
              inputDescription.editor!.validation.oneofKind === "regex"
                ? regexValidation(
                    value.value.string,
                    inputDescription.editor!.validation.regex,
                  )
                : "",
            )
            .filter((e) => e.length > 0);
          return errors.join(", ");
          // we can't validate not a string value
        }
        // We can validate only string value
        const valueString = values
          .map((value) =>
            value.value.oneofKind === "string" ? value.value.string : "",
          )
          .join("");
        return regexValidation(
          valueString,
          inputDescription.editor.validation.regex,
        );
      }
      // we can't validate not a string value
      break;
    }

    case "state":
      return stateValidation(inputDescription.editor.validation.state);
    case "maxStringLength":
      return maxStringLengthValidation(
        values,
        inputDescription.editor.validation.maxStringLength,
      );
    case "url":
      if (!inputDescription.editor.validation.url) {
        return "";
      }
      if (values.every((value) => value.value.oneofKind === "string")) {
        const valueString = values
          .map((value) =>
            value.value.oneofKind === "string" ? value.value.string : "",
          )
          .join("");
        return urlValidation(valueString);
      }
      break;
    default:
      // we don't have validation
      return "";
  }
  return "";
};

const checkInputsValidations = (
  inputs: InputCandidate[],
  inputsDescriptions: InputDescription[],
): boolean => {
  const isEveryValid = inputs.every((input) => {
    const inputDescription = inputsDescriptions.find(
      (description) => description.inputId === input.inputId,
    );
    if (inputDescription !== undefined) {
      if (checkInputValidation(inputDescription, input) !== "") {
        return false;
      }
    }

    return true;
  });

  return isEveryValid;
};

export const countInvalidInputsValidations = (
  inputs: InputConfig[],
  inputsDescriptions: InputDescription[],
  conditions?: ConditionsConfig,
): number => {
  let invalidInputs = 0;
  inputs.forEach((input) => {
    const inputDescription = inputsDescriptions.find(
      (description) => description.inputId === input.inputId,
    );
    if (inputDescription !== undefined) {
      if (checkInputValidation(inputDescription, input) !== "") {
        invalidInputs = ++invalidInputs;
      }
    }
  });
  if (R.isTruthy(conditions)) {
    invalidInputs += R.pipe(
      conditions.group,
      R.map((condition) =>
        // @ts-expect-error
        isConditionFnValid(condition.group?.condition.function?.function),
      ),
      R.countBy((isValid) => !isValid),
    );
  }

  return invalidInputs;
};

/**
 * Checks if a create-record or update-record action has at least one field
 * in the fields group with a value.
 * Returns true for all other action types (no validation needed).
 */
export const hasFieldsGroupWithValues = (action: ActionCandidate): boolean => {
  const actionId = action.actionReference?.actionId;

  if (!isRecordAction(actionId)) {
    return true;
  }

  const fieldsWithValuesCount = countFieldsGroupWithValues(
    action.inputs,
    action.description.inputs,
  );

  return fieldsWithValuesCount > 0;
};

export const candidateActionSourcesValid = (
  candidateStore: CandidateStore,
): boolean => {
  return candidateStore.allActions.every((action: ActionCandidate) => {
    if (action.accessError === true) {
      return true;
    }

    if (action.description.actionId === "" || action.credentialId === "") {
      return false;
    }

    // Checked ahead of the `recordListInput` guard below: an action whose
    // dynamic description has not loaded has no `recordListInput` yet, and that
    // is exactly when RecordsSourceEditor never renders to write a default.
    if (recordSourceMissing(action)) {
      return false;
    }

    if (action.description.recordListInput === undefined) {
      return true;
    }

    // The ENCLOSING loop, climbing past branch containers: an action inside
    // a branch that sits inside a loop still validates against that loop's
    // source (mirrors RecordsSourceEditor's option computation).
    const loopInstanceId = locateEnclosingLoop(
      candidateStore.self.actionGroups,
      action.hash,
    );
    const actionGroupType = loopInstanceId !== undefined ? "loop" : "actions";

    const findRecordsActions = candidateStore.findActionsWithRecordsList(
      action,
      actionGroupType,
    );

    const actionOptions = findRecordsActionsToOptions(findRecordsActions);
    const triggerOption = triggerToOption(candidateStore.self.trigger, action);

    return !(actionOptions.length === 0 && triggerOption === undefined);
  });
};

const candidateActionValid = (candidateStore: CandidateStore): boolean => {
  // Is at least one action exist
  if (candidateStore.allActions.length === 0) {
    return false;
  }

  // Is all action inputs validation passed?
  return candidateStore.allActions.every((action: ActionCandidate) => {
    // if no access - always valid
    if (action.accessError === true) {
      return true;
    }
    // if description is still not loaded.
    if (action.description.actionId === "" || action.credentialId === "") {
      return false;
    }
    // check if actionId valid for this trigger
    if (
      !triggersIdsAvailableForMergeRecordsAction.includes(
        candidateStore.self.trigger.triggerReference?.triggerId ?? "",
      ) &&
      action.actionReference?.actionId === "merge-records"
    ) {
      return false;
    }
    // check if create-record/update-record actions have at least one field
    if (!hasFieldsGroupWithValues(action)) {
      return false;
    }
    return checkInputsValidations(action.inputs, action.description.inputs);
  });
};

export const candidateValidation = (store: CandidateStore): boolean => {
  return (
    candidateTriggerValid(store) &&
    candidateActionValid(store) &&
    candidateConditionsValid(store) &&
    candidateFieldMissingValid(store.self) &&
    loopingActionsValid(store) &&
    candidateActionSourcesValid(store) &&
    branchesValid(store) &&
    branchNamesValid(store) &&
    branchesContentValid(store) &&
    branchesHaveConditions(store)
  );
};

const candidateConditionsValid = (candidateStore: CandidateStore): boolean => {
  return candidateStore.allActions.every((action: ActionCandidate) => {
    if (action.description.conditionsDisplay?.required === true) {
      return (action.conditions?.group ?? []).length > 0;
    }
    return true;
  });
};

/** Existing loopingActionsValid rule, addressed at a single loop group -- the
 * transplant is 1:1 with the previous root-only implementation, with
 * `candidateStore.looping.source`/`loopActions` replaced by the loop group's
 * own `source`/actions so the same rule applies to any nested loop.
 *
 * `hasActions` counts DEEP content (any action anywhere inside the loop
 * body, including one nested inside a branches block), not just the loop's
 * own direct "actions" group: the backend engine validates containers
 * recursively, so a loop whose only content is a nested CL block with
 * actions is a config the engine accepts, and must not block save here. */
const loopGroupValid = (
  candidateStore: CandidateStore,
  loopGroup: ActionGroupCandidate & { config: { oneofKind: "loop" } },
): boolean => {
  const hasActions =
    collectActionsDeep(loopGroup.config.loop.actionGroups).length > 0;
  const source = loopGroup.config.loop.source;

  if (source?.oneofKind === "recordList") {
    const isRecordList = !!source;
    return isRecordList && hasActions;
  }

  if (source?.oneofKind === "outputReference") {
    const loopOutputReference = source.outputReference;
    const outputId = loopOutputReference.outputId;

    switch (loopOutputReference.source.oneofKind) {
      case "actionReference": {
        const { actionReference } = loopOutputReference.source;
        const refAction = candidateStore.allActions.find(
          (action) =>
            action.actionReference !== undefined &&
            compareActionReferences(action.actionReference, actionReference),
        );
        const isOutputReference =
          !!outputId &&
          !!refAction &&
          getOutputDescriptions(refAction).some(
            (output) => output.outputId === outputId,
          );

        return isOutputReference && hasActions;
      }
      case "triggerReference": {
        const triggerOutput = getOutputDescriptions(
          candidateStore.self.trigger,
        ).find((output) => output.outputId === outputId);
        return !!triggerOutput && hasActions;
      }
      case "loopItem":
      default:
        return false;
    }
  }

  return hasActions;
};

/** Validates every loop group in the tree (root-level and nested inside
 * branches/loops) against the same rule: it must have both a configured
 * source and at least one action anywhere in its deep content (a nested
 * branches block with actions satisfies it -- see loopGroupValid). */
export const loopingActionsValid = (
  candidateStore: CandidateStore,
): boolean => {
  let isValid = true;
  walkActionGroups(candidateStore.self.actionGroups, (group) => {
    if (group.config.oneofKind !== "loop") return;
    if (!loopGroupValid(candidateStore, group as never)) isValid = false;
  });
  return isValid;
};

/**
 * Reference-mode branch conditions (Task 9, CL left-side sources) store the
 * left side as an `outputReference` instead of `conditionFieldId` (Trigger/Find
 * conditions still use `conditionFieldId` -- unaffected). A condition needs
 * SOME left side to be meaningful, and a reference must actually resolve
 * (the referenced action/field can be deleted after the fact) -- both are
 * blocking, unlike the value-completeness checks below which only look at the
 * right side.
 */
const branchConditionHasResolvableLeftSide = (
  candidateStore: CandidateStore,
  condition: ConditionsConfig_ConditionConfig,
): boolean => {
  if (condition.outputReference !== undefined) {
    return (
      resolveReferencedConditionField(
        candidateStore,
        condition.outputReference,
      ) !== undefined
    );
  }
  return condition.conditionFieldId !== "";
};

const branchConditionValid = (
  candidateStore: CandidateStore,
  condition: ConditionsConfig_ConditionsConfigGroup,
): boolean => {
  if (condition.group.oneofKind !== "condition") {
    return true;
  }
  if (
    !branchConditionHasResolvableLeftSide(
      candidateStore,
      condition.group.condition,
    )
  ) {
    return false;
  }
  const fn = condition.group.condition.function?.function;
  return (
    // @ts-expect-error -- fn may be undefined; isConditionFnValid handles it (see triggerConditionsValid above)
    isConditionFnValid(fn) &&
    getValueConfigByConditionFn(fn).every((v: ValueConfig_V) =>
      isMemberValueConfigValid(v, hostMemberScope),
    )
  );
};

/**
 * Branches don't require conditions (an unconditional branch always
 * executes) and don't require non-empty labels (both are legal on the wire).
 * This only checks value-completeness of filled-in condition rows, mirroring
 * candidateTriggerConditionsValid's value walk but without the trigger/action
 * output-existence checks -- those don't apply here (beyond the left-side
 * presence/resolvability check in branchConditionValid).
 */
export const branchesValid = (candidateStore: CandidateStore): boolean => {
  let isValid = true;

  walkActionGroups(candidateStore.self.actionGroups, (group) => {
    if (group.config.oneofKind !== "branches") {
      return;
    }
    const hasInvalidBranch = group.config.branches.branches.some(
      (branch) =>
        !(branch.conditions?.group ?? []).every((condition) =>
          branchConditionValid(candidateStore, condition),
        ),
    );
    if (hasInvalidBranch) {
      isValid = false;
    }
  });

  return isValid;
};

/**
 * Blocking validation: the engine rejects a branch/else-branch container
 * that has no non-empty action group anywhere inside it (`Actions group must
 * contain at least one action`, raised for the branch's own empty direct
 * group once `stripEmptyActionsGroups` removes it on release). Checked deep
 * (any action nested inside the branch, including inside a further-nested
 * loop or CL block) since the engine validates containers recursively -- a
 * branch whose only content is a nested loop with actions is valid.
 */
export const branchesContentValid = (
  candidateStore: CandidateStore,
): boolean => {
  let isValid = true;

  walkActionGroups(candidateStore.self.actionGroups, (group) => {
    if (group.config.oneofKind !== "branches") {
      return;
    }
    const { branches } = group.config;
    const allBranches: Array<{ actionGroups: ActionGroupCandidate[] }> = [
      ...branches.branches,
      ...(branches.elseBranch !== undefined ? [branches.elseBranch] : []),
    ];
    if (
      allBranches.some(
        (branch) => collectActionsDeep(branch.actionGroups).length === 0,
      )
    ) {
      isValid = false;
    }
  });

  return isValid;
};

/**
 * Blocking: every "If conditions are met" branch (the entries in `branches[]`)
 * must have at least one condition. The else-branch is excluded -- it lives in
 * `elseBranch`, is "If no conditions are met" by design, and carries no
 * conditions. A conditions-branch with an empty condition set is ambiguous
 * (nothing distinguishes it), so it can't be saved: the user must add a
 * condition or switch the branch to else mode. Checked deep so nested CL blocks
 * are covered too.
 */
export const branchesHaveConditions = (
  candidateStore: CandidateStore,
): boolean => {
  let isValid = true;

  walkActionGroups(candidateStore.self.actionGroups, (group) => {
    if (group.config.oneofKind !== "branches") {
      return;
    }
    const hasConditionlessBranch = group.config.branches.branches.some(
      (branch) => (branch.conditions?.group ?? []).length === 0,
    );
    if (hasConditionlessBranch) {
      isValid = false;
    }
  });

  return isValid;
};

export type BranchNameIssue = "empty" | "duplicate";

/**
 * Per-branch name issues within one block, keyed by branch hash (the
 * else-branch keeps its own hash). Trimmed, case-sensitive: a branch is
 * "empty" when its trimmed label is blank, "duplicate" when another branch
 * (or the else-branch) in the same block carries the same trimmed label.
 * Single source of the branch-name rules -- consumed by branchNamesValid
 * (the save gate) and by BranchSection's inline error display, so the two
 * can't drift.
 */
export const getBranchNameIssues = (
  block: BranchesActionGroupCandidate,
): Map<string, BranchNameIssue> => {
  const entries = [
    ...block.branches.map((branch) => ({
      hash: branch.hash,
      label: branch.label.trim(),
    })),
    ...(block.elseBranch !== undefined
      ? [
          {
            hash: block.elseBranch.hash,
            label: block.elseBranch.label.trim(),
          },
        ]
      : []),
  ];

  const labelCounts = new Map<string, number>();
  entries.forEach(({ label }) => {
    labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
  });

  const issues = new Map<string, BranchNameIssue>();
  entries.forEach(({ hash, label }) => {
    if (label === "") {
      issues.set(hash, "empty");
    } else if ((labelCounts.get(label) ?? 0) > 1) {
      issues.set(hash, "duplicate");
    }
  });
  return issues;
};

/**
 * Blocking validation (product decision, Етап 5): every branch in every
 * block must have a non-empty label that is unique within its block.
 * Empty/duplicate names block save and activation.
 */
export const branchNamesValid = (candidateStore: CandidateStore): boolean => {
  let isValid = true;

  walkActionGroups(candidateStore.self.actionGroups, (group) => {
    if (group.config.oneofKind !== "branches") {
      return;
    }
    if (getBranchNameIssues(group.config.branches).size > 0) {
      isValid = false;
    }
  });

  return isValid;
};

export type BranchValidationIssue =
  | "name-empty"
  | "name-duplicate"
  | "conditions-missing"
  | "conditions-invalid"
  | "no-content";

/**
 * The first blocking validation issue for a single branch (a conditions branch
 * or the else-branch), or undefined when the branch is valid. Mirrors the
 * branch-level save-gate rules -- branch name (branchNamesValid), required
 * conditions (branchesHaveConditions), condition value-completeness
 * (branchesValid) and non-empty content (branchesContentValid) -- so the canvas
 * validation triangle and the save gate can never disagree. `branchHash` is a
 * real branch hash or the literal "else".
 */
export const getBranchValidationIssue = (
  candidateStore: CandidateStore,
  block: BranchesActionGroupCandidate,
  branchHash: string,
): BranchValidationIssue | undefined => {
  const isElse = branchHash === "else";
  const branch = isElse
    ? block.elseBranch
    : block.branches.find((b) => b.hash === branchHash);
  if (branch === undefined) {
    return undefined;
  }

  const nameIssue = getBranchNameIssues(block).get(branch.hash);
  if (nameIssue === "empty") return "name-empty";
  if (nameIssue === "duplicate") return "name-duplicate";

  if (!isElse) {
    const conditions = (branch as BranchCandidate).conditions?.group ?? [];
    if (conditions.length === 0) {
      return "conditions-missing";
    }
    if (
      !conditions.every((condition) =>
        branchConditionValid(candidateStore, condition),
      )
    ) {
      return "conditions-invalid";
    }
  }

  if (collectActionsDeep(branch.actionGroups).length === 0) {
    return "no-content";
  }

  return undefined;
};

/**
 * True when any branch (including the else-branch) in the block has a blocking
 * validation issue -- drives the block-level validation triangle on the canvas
 * so users can spot the offending Conditional Logic block without opening it.
 */
export const blockHasValidationIssue = (
  candidateStore: CandidateStore,
  block: BranchesActionGroupCandidate,
): boolean => {
  const branchHashes = [
    ...block.branches.map((b) => b.hash),
    ...(block.elseBranch !== undefined ? ["else"] : []),
  ];
  return branchHashes.some(
    (hash) =>
      getBranchValidationIssue(candidateStore, block, hash) !== undefined,
  );
};

const candidateFieldMissingValid = (
  candidate: AutomationCandidate,
): boolean => {
  return (
    candidateTriggerInputValid(candidate) &&
    candidateActionInputReferenceValid(candidate) &&
    candidateActionConditionsValid(candidate) &&
    candidateTriggerConditionsValid(candidate) &&
    candidateActionInputValid()
  );
};

export const candidateActionInputValid = (
  actionCandidate?: ActionCandidate,
): boolean => {
  const actions = rootStore.automations.candidate.allActions;

  return actions
    .filter((a: ActionCandidate) =>
      actionCandidate !== undefined ? a.hash === actionCandidate.hash : true,
    )
    .every((action: ActionCandidate) => {
      const descriptions = new Set(
        action.description.inputs.map((o) => o.inputId),
      );

      if (descriptions.size === 0) {
        return true;
      }

      // AI Assist backward compatibility: persisted `model` input may exist without a matching
      // description row when dynamic descriptions omit legacy fields.
      const hasAllRequiredInputs = getFilteredInputs(action.inputs).every(
        (input: InputConfig) =>
          input.inputId === "model" || descriptions.has(input.inputId),
      );

      const hasValidSelectAndLimitAndSortInputs = action.inputs.every(
        (input) => {
          const inputDescription = action.description.inputs.find(
            (description) => description.inputId === input.inputId,
          );

          if (
            inputDescription?.editor?.editor.oneofKind !== "limitAndSort" &&
            inputDescription?.editor?.editor.oneofKind !== "select" &&
            inputDescription?.editor?.editor.oneofKind !== "keyValue"
          ) {
            return true;
          }

          return checkInputValidation(inputDescription, input) === "";
        },
      );

      return hasAllRequiredInputs && hasValidSelectAndLimitAndSortInputs;
    });
};

export const candidateTriggerInputValid = (
  candidate: AutomationCandidate,
): boolean => {
  const outputs = getOutputDescriptions(candidate.trigger);

  if (outputs.length === 0) {
    return true;
  }

  return getFilteredInputs(candidate.trigger.inputs).every(
    (input: InputConfig) => {
      const desc = candidate.trigger.description.inputs.find(
        (i) => i.inputId === input.inputId,
      );
      return (
        outputs.some((output: OutputDescription) =>
          output.outputId.includes(".")
            ? output.outputId.startsWith(input.inputId)
            : output.outputId === input.inputId,
        ) || desc?.editor?.editor.oneofKind === "hidden"
      );
    },
  );
};

export const candidateTriggerConditionsValid = (
  candidate: AutomationCandidate,
): boolean => {
  const triggerOutputs = getOutputDescriptions(candidate.trigger, true);

  if (triggerOutputs.length === 0) {
    return true;
  }
  return (candidate.trigger.conditions?.group ?? []).every(
    (group: ConditionsConfig_ConditionsConfigGroup) => {
      if (group.group.oneofKind === "condition") {
        const isPresent = triggerOutputs.find(
          (o: OutputDescription) =>
            o.outputId ===
            (group.group.oneofKind === "condition"
              ? group.group.condition.conditionFieldId
              : undefined),
        );
        const values = getValueConfigByConditionFn(
          group.group.condition.function?.function,
        );

        return (
          !!isPresent &&
          values.every((v: ValueConfig_V) => {
            if (v.value.oneofKind === "option") {
              if (group.group?.oneofKind === "condition") {
                const isExists = isOptionExists(
                  candidate,
                  group.group?.condition.conditionFieldId,
                  v.value.option.value,
                  undefined,
                  true,
                );
                return isExists;
              }
            }

            if (v.value.oneofKind === "reference") {
              if (v.value.reference.source.oneofKind === "triggerReference") {
                return !!triggerOutputs.find(
                  (output: OutputDescription) =>
                    output.outputId ===
                    (v.value.oneofKind === "reference"
                      ? v.value.reference.outputId
                      : undefined),
                );
              }
            }
            const result = isMemberValueConfigValid(v, hostMemberScope);
            if (!result) {
              return false;
            }
            return true;
          })
        );
      }
      return true;
    },
  );
};

export const candidateActionInputReferenceValid = (
  candidate: AutomationCandidate,
  actionCandidate?: ActionCandidate,
): boolean => {
  const actions = rootStore.automations.candidate.allActions;
  const rootLooping = rootStore.automations.candidate.looping;

  return actions
    .filter((a: ActionCandidate) =>
      actionCandidate !== undefined ? a.hash === actionCandidate.hash : true,
    )
    .every((action: ActionCandidate) => {
      const triggerOutputs = getOutputDescriptions(candidate.trigger);

      if (triggerOutputs.length === 0) {
        return true;
      }

      // Resolve currentItem references against the loop this action actually
      // lives in, falling back to the root loop for actions outside any loop
      // (parity with the pre-nested-loop behavior).
      const looping = rootLooping.scopeForAction(action.hash) ?? rootLooping;

      return getFilteredInputs(action.inputs).every((input: InputConfig) =>
        checkActionInput(
          input,
          triggerOutputs,
          candidate,
          action.actionReference,
          undefined,
          action,
          looping,
        ),
      );
    });
};

export const checkActionInput = (
  input: InputConfig,
  triggerOutputs: OutputDescription[],
  candidate: AutomationCandidate,
  actionReference?: ActionReference,
  isCondition?: boolean,
  actionCandidate?: ActionCandidate,
  looping?: LoopScope,
): boolean => {
  const unwrapped =
    input.value !== undefined ? unwrapValueConfig(input.value) : undefined;
  // Lazy: an input holding no member or team must not touch the stores at all.
  const getScope = lazyScope(() =>
    memberScopeForInput(
      rootStore,
      rootStore.config.solutionId,
      input.inputId,
      actionCandidate,
    ),
  );
  return (unwrapped?.values ?? []).every((v) => {
    if (v.value.oneofKind === "reference") {
      const currentItemPresent = !!isCurrentItemPresent(
        v.value.reference.outputId,
        looping,
      );
      if (currentItemPresent) {
        return true;
      }

      if (v.value.reference.source.oneofKind === "triggerReference") {
        return !!triggerOutputs.find(
          (output: OutputDescription) =>
            output.outputId ===
              (v.value.oneofKind === "reference"
                ? v.value.reference.outputId
                : undefined) &&
            v.value.oneofKind === "reference" &&
            v.value.reference.source.oneofKind === "triggerReference" &&
            v.value.reference.source.triggerReference.triggerId ===
              candidate.trigger.triggerReference?.triggerId,
        );
      }
      if (v.value.reference.source.oneofKind === "actionReference") {
        const actions = rootStore.automations.candidate.allActions;
        const refAction = actions.find(
          (action: ActionCandidate) =>
            v.value.oneofKind === "reference" &&
            v.value.reference.source.oneofKind === "actionReference" &&
            action.actionReference?.actionId ===
              v.value.reference.source.actionReference?.actionId &&
            action.actionReference?.instanceId ===
              v.value.reference.source.actionReference.instanceId,
        );
        if (refAction === undefined) {
          return false;
        }

        const output = getOutputDescriptions(refAction);

        return !!output.find(
          (output: OutputDescription) =>
            output.outputId ===
            (v.value.oneofKind === "reference"
              ? v.value.reference.outputId
              : undefined),
        );
      }

      return false;
    }
    const result = isMemberValueConfigValid(v, getScope);
    if (!result) {
      return false;
    }
    if (v.value.oneofKind === "smartdoc") {
      const smartdoc = parseSmartDoc(v.value.smartdoc);
      return smartdoc.content.every((content: ContentItem) => {
        return (content.content ?? []).every((c: ContentItem) => {
          return checkContent(
            c,
            triggerOutputs,
            candidate,
            actionCandidate,
            looping,
          );
        });
      });
    }

    if (v.value.oneofKind === "option") {
      const isExists = isOptionExists(
        candidate,
        input.inputId,
        v.value.option.value,
        actionReference,
        isCondition,
      );
      return isExists;
    }

    if (v.value.oneofKind === "keyValue") {
      return checkActionInput(
        {
          ...input,
          value: v.value.keyValue.value,
        },
        triggerOutputs,
        candidate,
        actionReference,
        isCondition,
        actionCandidate,
        looping,
      );
    }

    if (v.value.oneofKind === "list") {
      return v.value.list.values.every((lv) =>
        checkActionInput(
          {
            ...input,
            value: lv,
          },
          triggerOutputs,
          candidate,
          actionReference,
          isCondition,
          actionCandidate,
          looping,
        ),
      );
    }
    return true;
  });
};

export const isCurrentItemPresent = (
  outputId: string,
  loopScope: LoopScope = rootStore.automations.candidate.looping,
): DynamicSectionOption | boolean | undefined => {
  if (outputId === CURRENT_ITEM_OUTPUT_ID) {
    return true;
  }
  if (outputId.match(/^currentItem[.|]/)) {
    const actions = rootStore.automations.candidate.allActions;
    const trigger = rootStore.automations.candidate.self.trigger;

    const dynamicSection = memoizedConvertOutputToDynamicSectionOption(
      loopScope,
      actions,
      trigger,
    );

    return dynamicSection?.options.find(
      (option) => option.outputId === outputId,
    );
  }
};

const checkContent = (
  content: ContentItem,
  triggerOutputs: OutputDescription[],
  candidate: AutomationCandidate,
  actionCandidate?: ActionCandidate,
  looping?: LoopScope,
  includeNestedHref = true,
): boolean => {
  const actions = rootStore.automations.candidate.allActions;

  if (content.type === "list_item") {
    if (content.content == null) {
      return false;
    }
    return content.content?.every((c: ContentItem) => {
      return (c.content ?? []).every((cont: ContentItem) => {
        return checkContent(
          cont,
          triggerOutputs,
          candidate,
          actionCandidate,
          looping,
          includeNestedHref,
        );
      });
    });
  }

  if (content.type === "pill") {
    try {
      const value: OutputReference = JSON.parse(String(content.attrs?.value));
      if (value == null || value.source == null) {
        return false;
      }
      if (value.source.oneofKind === "triggerReference") {
        const currentItemPresent = !!isCurrentItemPresent(
          value.outputId,
          looping,
        );

        if (currentItemPresent) {
          return true;
        }

        return !!triggerOutputs.find(
          (output: OutputDescription) =>
            output.outputId === value.outputId &&
            value.source.oneofKind === "triggerReference" &&
            value.source.triggerReference.triggerId ===
              candidate.trigger.triggerReference?.triggerId,
        );
      }
      if (value.source.oneofKind === "actionReference") {
        const refAction = actions.find(
          (action: ActionCandidate) =>
            value.source.oneofKind === "actionReference" &&
            action.actionReference?.actionId ===
              value.source.actionReference.actionId &&
            action.actionReference?.instanceId ===
              value.source.actionReference.instanceId,
        );

        if (
          refAction === undefined ||
          !isSupportedOutput(refAction, actions, actionCandidate, looping)
        ) {
          return false;
        }

        const currentItemPresent = !!isCurrentItemPresent(
          value.outputId,
          looping,
        );

        if (currentItemPresent) {
          return true;
        }

        const output = getOutputDescriptions(refAction);

        return !!output.find(
          (output: OutputDescription) => output.outputId === value.outputId,
        );
      }
    } catch (_) {
      return false;
    }
  }

  if (
    includeNestedHref &&
    content.marks !== undefined &&
    content.marks.length > 0
  ) {
    return content.marks.every((mark) => {
      if (mark.type !== "link" || !mark.attrs?.href) {
        return true;
      }
      try {
        const hrefDoc = JSON.parse(String(mark.attrs.href)) as Doc;
        if (hrefDoc?.type !== "doc") {
          return true;
        }
        return (hrefDoc.content ?? []).every((block: ContentItem) =>
          (block.content ?? []).every((node: ContentItem) =>
            checkContent(
              node,
              triggerOutputs,
              candidate,
              actionCandidate,
              looping,
              includeNestedHref,
            ),
          ),
        );
      } catch {
        return true;
      }
    });
  }

  return true;
};

/**
 * True when the input has a smartdoc value where the main document validates but
 * a nested serialized doc inside a link `href` (URL-with-pills) does not.
 */
export const isSmartdocFieldMissingNestedHrefOnly = (
  input: InputConfig,
  triggerOutputs: OutputDescription[],
  candidate: AutomationCandidate,
  actionCandidate?: ActionCandidate,
  looping?: LoopScope,
): boolean => {
  const unwrapped =
    input.value !== undefined ? unwrapValueConfig(input.value) : undefined;
  for (const v of unwrapped?.values ?? []) {
    if (v.value.oneofKind !== "smartdoc") {
      continue;
    }
    const smartdoc = parseSmartDoc(v.value.smartdoc);
    const surfaceOk = smartdoc.content.every((block: ContentItem) =>
      (block.content ?? []).every((c: ContentItem) =>
        checkContent(
          c,
          triggerOutputs,
          candidate,
          actionCandidate,
          looping,
          false,
        ),
      ),
    );
    const fullOk = smartdoc.content.every((block: ContentItem) =>
      (block.content ?? []).every((c: ContentItem) =>
        checkContent(
          c,
          triggerOutputs,
          candidate,
          actionCandidate,
          looping,
          true,
        ),
      ),
    );
    if (surfaceOk && !fullOk) {
      return true;
    }
  }
  return false;
};

export const candidateActionConditionsValid = (
  candidate: AutomationCandidate,
  actionCandidate?: ActionCandidate,
  looping: LoopScope = rootStore.automations.candidate.looping,
): boolean => {
  const actions = rootStore.automations.candidate.allActions;

  return actions
    .filter((a: ActionCandidate) =>
      actionCandidate !== undefined ? a.hash === actionCandidate.hash : true,
    )
    .every((action: ActionCandidate) => {
      const actionOutputs = getOutputDescriptions(action, true);
      const triggerOutputs = getOutputDescriptions(candidate.trigger, true);

      if (triggerOutputs.length === 0 || actionOutputs.length === 0) {
        return true;
      }

      // Resolve currentItem references against the loop this action actually
      // lives in, falling back to the caller-provided/root scope for actions
      // outside any loop (parity with the pre-nested-loop behavior).
      const actionLooping =
        rootStore.automations.candidate.looping.scopeForAction(action.hash) ??
        looping;

      return (action.conditions?.group ?? []).every(
        (group: ConditionsConfig_ConditionsConfigGroup) => {
          if (group.group.oneofKind === "condition") {
            const isPresent = actionOutputs.find(
              (o: OutputDescription) =>
                o.outputId ===
                (group.group.oneofKind === "condition"
                  ? group.group.condition.conditionFieldId
                  : undefined),
            );
            const values = getValueConfigByConditionFn(
              group.group.condition.function?.function,
            );
            return (
              !!isPresent &&
              values.every((v: ValueConfig_V) => {
                if (v.value.oneofKind === "reference") {
                  if (
                    v.value.reference.source.oneofKind === "triggerReference"
                  ) {
                    const outputId = v.value.reference.outputId;

                    if (outputId.match(/^currentItem/)) {
                      if (outputId === "currentItem") {
                        return (
                          actionLooping.source?.oneofKind ===
                            "outputReference" &&
                          actionLooping.source?.outputReference?.source
                            .oneofKind === "triggerReference" &&
                          actionLooping.source?.outputReference?.source
                            .triggerReference?.triggerId ===
                            v.value.reference.source.triggerReference?.triggerId
                        );
                      }

                      const outputOptions =
                        memoizedConvertOutputToDynamicSectionOption(
                          actionLooping,
                          actions,
                          candidate.trigger,
                        );

                      const outputOption = outputOptions?.options.find(
                        (option) => option.outputId === outputId,
                      );

                      if (outputOption) {
                        return true;
                      }
                    }

                    return !!triggerOutputs.find(
                      (output: OutputDescription) =>
                        output.outputId === outputId,
                    );
                  }
                  if (
                    v.value.reference.source.oneofKind === "actionReference"
                  ) {
                    const refAction = actions.find(
                      (action: ActionCandidate) =>
                        v.value.oneofKind === "reference" &&
                        v.value.reference.source.oneofKind ===
                          "actionReference" &&
                        action.actionReference?.actionId ===
                          v.value.reference.source.actionReference.actionId &&
                        action.actionReference?.instanceId ===
                          v.value.reference.source.actionReference.instanceId,
                    );

                    if (
                      refAction === undefined ||
                      !isSupportedOutput(
                        refAction,
                        actions,
                        actionCandidate,
                        actionLooping,
                      )
                    ) {
                      return false;
                    }

                    const outputId = v.value.reference.outputId;

                    if (outputId.match(/^currentItem/)) {
                      if (outputId === "currentItem") {
                        return (
                          actionLooping.source?.oneofKind ===
                            "outputReference" &&
                          actionLooping.source?.outputReference?.source
                            .oneofKind === "actionReference" &&
                          actionLooping.source?.outputReference?.source
                            .actionReference?.actionId ===
                            v.value.reference.source.actionReference.actionId &&
                          actionLooping.source?.outputReference?.source
                            .actionReference?.instanceId ===
                            v.value.reference.source.actionReference.instanceId
                        );
                      }

                      const outputOptions =
                        memoizedConvertOutputToDynamicSectionOption(
                          actionLooping,
                          actions,
                          candidate.trigger,
                        );

                      const outputOption = outputOptions?.options.find(
                        (option) => option.outputId === outputId,
                      );

                      if (outputOption) {
                        return true;
                      }
                    }

                    const output = getOutputDescriptions(refAction);
                    return !!output.find(
                      (output: OutputDescription) =>
                        output.outputId === outputId,
                    );
                  }
                } else if (v.value.oneofKind === "option") {
                  if (group.group.oneofKind === "condition") {
                    const inputId = group.group.condition.conditionFieldId;
                    const isExists = isOptionExists(
                      candidate,
                      inputId,
                      v.value.option.value,
                      action.actionReference,
                      true,
                    );
                    return isExists;
                  }
                }
                return true;
              })
            );
          }
          return true;
        },
      );
    });
};

const FILTERED_INPUT_IDS = new Set<string>([
  "solution-id",
  "application-id",
  "record_id",
  "form-id",
  "schedule",
  "channel",
  "channel_dynamic",
  INPUT_ACTION_NAME_ID,
  FIND_RECORD_LIMIT_ID,
  "find-record-limit",
  WHEN_BUTTON_IS_CLICKED_BUTTON_SLUG,
  WHEN_BUTTON_IS_CLICKED_BUTTON_LABEL,
]);

export const getFilteredInputs = (inputs: InputConfig[]): InputConfig[] =>
  inputs.filter((input: InputConfig) => !FILTERED_INPUT_IDS.has(input.inputId));

export const actionValidation = (action: ActionCandidate): boolean => {
  return (
    checkInputsValidations(action.inputs, action.description.inputs) &&
    action.credentialId !== ""
  );
};

export const candidateTriggerValid = (store: CandidateStore): boolean => {
  // Is trigger exist?
  if (
    store.self.trigger.triggerReference?.triggerId === undefined ||
    store.self.trigger.triggerReference.triggerId === "" ||
    store.self.trigger.description.triggerId === "" ||
    store.self.trigger.credentialId === ""
  ) {
    return false;
  }

  if (store.triggerPermissionsError !== undefined) {
    return false;
  }

  // Matches Conditions Trigger should have at least one condition
  if (
    store.self.trigger.triggerReference.triggerId ===
      TriggerLabel.matchesCondition &&
    !triggerConditionsValid(store.self)
  ) {
    return false;
  }

  // Is all trigger inputs validation passed?
  return checkInputsValidations(
    store.self.trigger.inputs,
    store.self.trigger.description.inputs,
  );
};

const triggerConditionsValid = (candidate: AutomationCandidate): boolean =>
  R.isTruthy(
    candidate.trigger.conditions?.group.every((condition) =>
      // @ts-expect-error
      isConditionFnValid(condition.group?.condition.function?.function),
    ),
  );

export const isConditionFnValid = (
  fn: ConditionFunctionConfig["function"],
): boolean => {
  if (R.isTruthy(fn)) {
    switch (fn.oneofKind) {
      case "isEmpty":
      case "isNotEmpty":
      case "isAny":
      case "allItemsResolved":
      case "notAllItemsResolved":
      case "isComplete":
      case "isNotComplete": {
        // this conditions haven't any values
        return true;
      }
      case "isEquals":
      case "isNotEquals":
      case "isAnyOf":
      case "isNoneOf":
      case "greaterThan":
      case "greaterThanOrEquals":
      case "lessThan":
      case "lessThanOrEquals":
      case "contains":
      case "notContains":
      case "percentCompleteLessThan":
      case "percentCompleteGreaterThan": {
        // @ts-expect-error
        return fn?.[fn.oneofKind]?.right?.values?.length > 0;
      }
      case "hasAnyOf":
      case "hasAllOf":
      case "hasNoneOf": {
        // @ts-expect-error
        return fn?.[fn.oneofKind]?.haystack?.values?.length > 0;
      }
      case "changes": {
        return (
          // @ts-expect-error
          isConditionFnValid(fn.changes.before?.function) &&
          // @ts-expect-error
          isConditionFnValid(fn.changes.after?.function)
        );
      }
      default:
        return false;
    }
  } else {
    return false;
  }
};

export const getValueConfigByConditionFn = (
  fn?: ConditionFunctionConfig["function"],
): ValueConfig_V[] => {
  if (R.isTruthy(fn)) {
    switch (fn.oneofKind) {
      case "isEmpty":
      case "isNotEmpty":
      case "isAny":
      case "allItemsResolved":
      case "notAllItemsResolved":
      case "isComplete":
      case "isNotComplete": {
        return [];
      }
      case "isEquals":
      case "isNotEquals":
      case "isAnyOf":
      case "isNoneOf":
      case "greaterThan":
      case "greaterThanOrEquals":
      case "lessThan":
      case "lessThanOrEquals":
      case "contains":
      case "notContains":
      case "percentCompleteLessThan":
      case "percentCompleteGreaterThan": {
        // @ts-expect-error
        return fn?.[fn.oneofKind]?.right?.values ?? [];
      }
      case "hasAnyOf":
      case "hasAllOf":
      case "hasNoneOf": {
        // @ts-expect-error
        return fn?.[fn.oneofKind]?.haystack?.values ?? [];
      }
      case "changes": {
        return [
          ...getValueConfigByConditionFn(fn.changes.before?.function),
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          ...getValueConfigByConditionFn(fn.changes.after?.function),
        ];
      }
      default:
        return [];
    }
  } else {
    return [];
  }
};

/**
 * A saved member or team is valid only within the scope its own input offers --
 * a member who can't see the solution an action writes into can't hold that
 * record's user field. `scope` must therefore come from the input being checked,
 * never from the automation's hosting solution alone.
 */
const isMemberValueConfigValid = (
  v: ValueConfig_V,
  getScope: () => MemberScope,
): boolean => {
  if (v.value.oneofKind === "function") {
    const scope = getScope();
    const func = v.value.function.function;
    if (func.oneofKind === "memberName") {
      const member = scope.members.find(
        (item) => item.memberId === func.memberName.memberId,
      );
      return member !== undefined;
    }
    if (func.oneofKind === "teamMemberIds") {
      const team = scope.teams.find(
        (item) => item.teamId === func.teamMemberIds.teamId,
      );
      return team !== undefined;
    }
    if (func.oneofKind === "teamId") {
      const team = scope.teams.find(
        (item) => item.teamId === func.teamId.teamId,
      );
      return team !== undefined;
    }
  }
  return true;
};

/**
 * Trigger conditions and branch conditions belong to the automation itself, not
 * to an action writing elsewhere, so they resolve against the hosting solution.
 */
const hostMemberScope = (): MemberScope =>
  solutionMemberScope(rootStore, rootStore.config.solutionId);

/** Resolves the scope at most once, and only if a member or team is actually met. */
const lazyScope = (resolve: () => MemberScope): (() => MemberScope) => {
  let cached: MemberScope | undefined;

  return () => {
    if (cached === undefined) {
      cached = resolve();
    }
    return cached;
  };
};
